import {DashboardSeriesScanClass} from '../../dashboard-new/types/dashboard.types';
import {HexColor} from '../../shared/symbols';

/**
 * Describes a view region for the dashboard.
 * Each view region has a name, a list of related label ids and a list of child regions.
 * The related label ids are the ids of the labels that used to display the view region (related surfaces).
 * The child regions are the regions that are contained in the view region.
 */
export interface DashboardViewRegion {
  /** Unique identifier of the view region. */
  id: number;

  /** The name of the view region used to display it in the UI. */
  name: string;

  /**
   * Indicates whether the view region is unavailable for the user.
   * This usually happens when the user has no data for the view region.
   * The common reason is the lack of the necessary scan type.
   * For example, "Veins" view region is unavailable if the user has no SWI scan.
   */
  is_available: boolean;

  /** The reason why the view region is unavailable. */
  unavailability_reason: string;

  /**
   * Related body segment to be displayed in the viewer.
   * Currently, has only two values: "brain" and "full body".
   */
  body_segment: DashboardViewRegionBodySegment;

  /**
   * The position of the world center of the view region in the 3D viewer.
   */
  world_center_point_3D: DashboardViewRegionPosition;

  /** The initial zoom level for the view region in the 3D viewer. */
  zoom_level_3D: DashboardViewRegionZoom3D;

  /**
   * The position of the initial view point in the scan viewer.
   * Used to center the crosshairs on the view region on the region change.
   * Applicable for both the 3D and scan views.
   */
  view_point_position: DashboardViewRegionPosition;

  /** The list of related surface region data. */
  mri_labels: DashboardViewRegionMriLabel[];

  /** The list of child regions. */
  subregions: DashboardViewRegion[];
}

/**
 * To avoid duplicates of the MRI labels in the backend, they are provided as a separate model.
 */
export interface BackendViewRegion extends Omit<DashboardViewRegion, 'mri_labels' | 'subregions'> {
  /** Unique identifier of the related view region MRI labels. */
  mri_label_ids: DashboardViewRegionMriLabel['id'][];

  /** Child regions following the same structure as the parent region. */
  subregions: BackendViewRegion[];
}

/**
 * Describes the view region MRI label backend model.
 */
export interface DashboardViewRegionMriLabel {

  /** Unique identifier of the view region MRI label. */
  id: number;

  /**
   * Unique identifier of the related surface derivative file.
   */
  surface_derivative_id: number;

  /** Name of the view region surface. */
  name: string;

  /**
   * Color that will be applied to the surface and scan labels.
   */
  color: HexColor;

  /**
   * Used for selection which part of the surface should be highlighted.
   * If the filter color matches the color in the surface data, only the matched part will be highlighted.
   */
  filter_color: HexColor;

  /** Contains the opacity for the view region surfaces and scan labels. */
  opacity: number;

  /** Related surface label id region; used for selection the specific surface region in the 3D viewer. */
  surface_label: number;

  /**
   * Describes the scan type that needed to be approved to display the current view region.
   */
  scan_source: DashboardSeriesScanClass;

  /** The list of related scan labels. */
  scan_labels: number[];
}

/**
 * Describes the body segment for the dashboard view region.
 */
export enum DashboardViewRegionBodySegment {
  /** The brain segment. */
  Brain = 'brain',

  /** The full body segment. */
  FullBody = 'full_body',
}

/**
 * Describes the parent map for the dashboard view regions.
 * The result is a relation between the view region id and its parent id.
 */
export type DashboardViewRegionsParentMap = Map<DashboardViewRegion['id'], DashboardViewRegion['id']>;

/**
 * Describes the flat list map for the dashboard view regions.
 * The result is a relation between the view region id and the view region.
 */
export type DashboardViewRegionsFlatListMap = Map<DashboardViewRegion['id'], DashboardViewRegion>;

/**
 * Describes the view region zoom level.
 * The zoom level is used to determine the default zoom level of the view region in the viewer for 3D view.
 * The typical range is 200 - 400, but it can be higher / lower for very large / small regions.
 * The higher the zoom level, the further the camera is from the region.
 */
export type DashboardViewRegionZoom3D = number;

/**
 * Describes some coordinate point in the papaya viewer.
 * Applicable for both the 3D and scan views.
 */
export type DashboardViewRegionPosition = [number, number, number];
